import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import {
  getCustomRecipeDetails,
  updateCustomRecipe,
  uploadCustomRecipeImageFile,
} from '@/lib/apiService';
import { showErrorToast } from '@/utils/toast-utils';
import { Button } from '@/components/ui/button';
import { Loader2, ChefHat } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MEAL_TYPE_OPTIONS, DISH_TYPE_OPTIONS } from '@/constants/dietary-goals';
import { RecipeInformationForm } from '@/components/recipes/RecipeInformationForm';
import { RecipeTagsSection } from '@/components/recipes/RecipeTagsSection';
import { InstructionsSection } from '@/components/recipes/InstructionsSection';
import { IngredientsSection } from '@/components/recipes/IngredientsSection';
import { useIngredientManagement } from '@/hooks/recipes/useIngredientManagement';
import { RecipePortionSizesSection } from '@/components/recipes/RecipePortionSizesSection';
import { useRecipePortionSizes } from '@/hooks/recipes/useRecipePortionSizes';
import { getPublicRecipeNameConflictMessage } from '@/utils/recipe-conflict';
import {
  deriveAllergensFromIngredients,
  deriveIntolerancesFromIngredients,
  mergeRecipeAllergens,
  mergeRecipeIntolerances,
  normalizeRecipeAllergenList,
  normalizeRecipeIntoleranceList,
} from '@/utils/recipe-allergens';

function ensureStringArray(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => (typeof item === 'string' ? item : (item && typeof item === 'object' && ((item as any).value ?? (item as any).id ?? (item as any).name) != null) ? String((item as any).value ?? (item as any).id ?? (item as any).name) : '')).filter(Boolean);
  }
  return [];
}

const EditCustomRecipePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [duplicateRecipeMessage, setDuplicateRecipeMessage] = useState<string | null>(null);

  // Form state
  const [recipeName, setRecipeName] = useState('');
  const [aliases, setAliases] = useState('');
  const [description, setDescription] = useState('');
  const [servings, setServings] = useState<number | ''>(1);
  const [nutritionServings, setNutritionServings] = useState<number>(1);
  const [dietLabels, setDietLabels] = useState<string[]>([]);
  const [allergies, setAllergies] = useState<string[]>([]);
  const [intoleranceTags, setIntoleranceTags] = useState<string[]>([]);
  const [cuisineTypes, setCuisineTypes] = useState<string[]>([]);
  const [mealTypes, setMealTypes] = useState<string[]>([]);
  const [dishTypes, setDishTypes] = useState<string[]>([]);
  const [cookingMethod, setCookingMethod] = useState('');
  const [shareWithOthers, setShareWithOthers] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [cookingTips, setCookingTips] = useState('');
  const [prepTimeMinutes, setPrepTimeMinutes] = useState<number | ''>('');
  const [cookTimeMinutes, setCookTimeMinutes] = useState<number | ''>('');
  const [totalTimeMinutes, setTotalTimeMinutes] = useState<number>(0);
  const [recipeImagePreviewUrl, setRecipeImagePreviewUrl] = useState<string | null>(null);
  const recipeImageObjectUrlRef = useRef<string | null>(null);
  const recipeImageFileRef = useRef<File | null>(null);
  const initialRecipeImageUrlRef = useRef<string | null>(null);
  const recipeImageRemovedRef = useRef(false);
  const manualAllergiesRef = useRef<string[]>([]);
  const manualIntolerancesRef = useRef<string[]>([]);
  const autoManagedAllergiesRef = useRef<Set<string>>(new Set());
  const autoManagedIntolerancesRef = useRef<Set<string>>(new Set());
  
  const portionConfig = useRecipePortionSizes(dishTypes);
  const {
    portionSizeId,
    defaultServingGrams,
    portionSizes,
    customPortionName,
    customPortionGrams,
    customPortionMl,
    portionDensityGPerMl,
    setPortionSizeId,
    setDefaultServingGrams,
    setPortionDensityGPerMl,
    setCustomPortionName,
    setCustomPortionGrams,
    setCustomPortionMl,
    setPortionWeight,
    setPortionVolume,
    removePortion,
    addCustomPortion,
    hydrateFromServingSizes,
    resolvePortionGrams,
    resolvePortionMl,
    getPortionOptionValue,
  } = portionConfig;

  const buildServingSizes = () => {
    const selectedServingId = typeof portionSizeId === 'string' && portionSizeId.trim().length > 0
      ? portionSizeId.trim().toLowerCase()
      : null;
    const defaultServingGramsParsed = typeof defaultServingGrams === 'number' && defaultServingGrams > 0
      ? Math.round(defaultServingGrams * 100) / 100
      : null;
    
    const GRAM_SERVING_PATTERN = /^(g|gram|grams)$/i;
    const isGramServing = (value: unknown): boolean => {
      const normalized = String(value ?? '').trim();
      if (!normalized) return false;
      return GRAM_SERVING_PATTERN.test(normalized);
    };

    const selectedServingDefaultGrams = selectedServingId && isGramServing(selectedServingId)
      ? 1
      : defaultServingGramsParsed;

    const seen = new Set<string>();
    const sizes: any[] = [];
    for (const portion of portionSizes ?? []) {
      const servingIdRaw = portion.servingId ?? portion.id ?? portion.name;
      const servingId = typeof servingIdRaw === 'string' ? servingIdRaw.trim().toLowerCase() : '';
      if (!servingId || seen.has(servingId)) continue;
      const grams = resolvePortionGrams(portion);
      if (!Number.isFinite(grams) || (grams as number) <= 0) continue;
      seen.add(servingId);
      sizes.push({
        servingId,
        grams,
        ml: resolvePortionMl(portion) ?? undefined,
        densityGPerMl: Number.isFinite(Number(portion.densityGPerMl)) && Number(portion.densityGPerMl) > 0
          ? Math.round(Number(portion.densityGPerMl) * 10000) / 10000
          : undefined,
        quantity: Number.isFinite(Number(portion.quantity)) && Number(portion.quantity) > 0
          ? Math.round(Number(portion.quantity) * 100) / 100
          : 1,
        label: typeof portion.name === 'string' && portion.name.trim().length > 0 ? portion.name.trim() : undefined,
        isDefault: selectedServingId === servingId,
      });
    }

    if (selectedServingId) {
      const defaultIndex = sizes.findIndex((entry) => entry.servingId === selectedServingId);
      if (defaultIndex >= 0) {
        sizes[defaultIndex] = {
          ...sizes[defaultIndex],
          isDefault: true,
        };
      } else if (selectedServingDefaultGrams != null) {
        sizes.push({
          servingId: selectedServingId,
          grams: selectedServingDefaultGrams,
          isDefault: true,
        });
      }
    }

    return sizes.length > 0 ? sizes : [];
  };

  const hydrateFromServingSizesRef = useRef(hydrateFromServingSizes);

  useEffect(() => {
    hydrateFromServingSizesRef.current = hydrateFromServingSizes;
  }, [hydrateFromServingSizes]);

  const revokeRecipeImagePreview = useCallback(() => {
    if (recipeImageObjectUrlRef.current) {
      URL.revokeObjectURL(recipeImageObjectUrlRef.current);
      recipeImageObjectUrlRef.current = null;
    }
  }, []);

  useEffect(() => () => revokeRecipeImagePreview(), [revokeRecipeImagePreview]);

  const handleRecipeImageFileSelect = useCallback(
    (file: File | null) => {
      recipeImageRemovedRef.current = false;
      if (!file) {
        revokeRecipeImagePreview();
        recipeImageFileRef.current = null;
        if (initialRecipeImageUrlRef.current) {
          recipeImageRemovedRef.current = true;
        }
        setRecipeImagePreviewUrl(null);
        return;
      }
      const okType =
        file.type === 'image/jpeg'
        || file.type === 'image/jpg'
        || file.type === 'image/png'
        || file.type === 'image/webp';
      if (!okType) {
        showErrorToast(new Error('Please choose a JPEG, PNG, or WebP image.'), 'Invalid file');
        return;
      }
      const maxBytes = 5 * 1024 * 1024;
      if (file.size > maxBytes) {
        showErrorToast(new Error('Image must be 5MB or smaller.'), 'File too large');
        return;
      }
      revokeRecipeImagePreview();
      recipeImageFileRef.current = file;
      const url = URL.createObjectURL(file);
      recipeImageObjectUrlRef.current = url;
      setRecipeImagePreviewUrl(url);
    },
    [revokeRecipeImagePreview],
  );

  const ingredientManagement = useIngredientManagement();
  const ingredientDerivedAllergies = React.useMemo(
    () => deriveAllergensFromIngredients(ingredientManagement.ingredients),
    [ingredientManagement.ingredients],
  );
  const ingredientDerivedIntolerances = React.useMemo(
    () => deriveIntolerancesFromIngredients(ingredientManagement.ingredients),
    [ingredientManagement.ingredients],
  );

  const handleAllergiesChange = useCallback((values: string[]) => {
    const derivedSet = new Set(ingredientDerivedAllergies);
    const autoManagedSet = autoManagedAllergiesRef.current;
    const manualAllergies = normalizeRecipeAllergenList(values).filter((value) => (
      !derivedSet.has(value) && !autoManagedSet.has(value)
    ));
    manualAllergiesRef.current = manualAllergies;
    setAllergies(mergeRecipeAllergens(manualAllergies, ingredientDerivedAllergies));
  }, [ingredientDerivedAllergies]);

  const handleIntoleranceTagsChange = useCallback((values: string[]) => {
    const derivedSet = new Set(ingredientDerivedIntolerances);
    const autoManagedSet = autoManagedIntolerancesRef.current;
    const manualIntolerances = normalizeRecipeIntoleranceList(values).filter((value) => (
      !derivedSet.has(value) && !autoManagedSet.has(value)
    ));
    manualIntolerancesRef.current = manualIntolerances;
    setIntoleranceTags(mergeRecipeIntolerances(manualIntolerances, ingredientDerivedIntolerances));
  }, [ingredientDerivedIntolerances]);

  useEffect(() => {
    const autoManagedSet = autoManagedAllergiesRef.current;
    manualAllergiesRef.current = normalizeRecipeAllergenList(manualAllergiesRef.current)
      .filter((value) => !autoManagedSet.has(value));

    const manualSet = new Set(manualAllergiesRef.current);
    autoManagedAllergiesRef.current = new Set(
      ingredientDerivedAllergies.filter((value) => !manualSet.has(value)),
    );

    setAllergies(mergeRecipeAllergens(manualAllergiesRef.current, ingredientDerivedAllergies));
  }, [ingredientDerivedAllergies]);

  useEffect(() => {
    const autoManagedSet = autoManagedIntolerancesRef.current;
    manualIntolerancesRef.current = normalizeRecipeIntoleranceList(manualIntolerancesRef.current)
      .filter((value) => !autoManagedSet.has(value));

    const manualSet = new Set(manualIntolerancesRef.current);
    autoManagedIntolerancesRef.current = new Set(
      ingredientDerivedIntolerances.filter((value) => !manualSet.has(value)),
    );

    setIntoleranceTags(mergeRecipeIntolerances(manualIntolerancesRef.current, ingredientDerivedIntolerances));
  }, [ingredientDerivedIntolerances]);

  // Load recipe data on mount
  useEffect(() => {
    async function fetchRecipe() {
      if (!id) return;
      
      setLoading(true);
      try {
        const response = await getCustomRecipeDetails(id);
        
        if (response.success && response.data) {
          const recipe = response.data;

          revokeRecipeImagePreview();
          const imgUrl = recipe.recipeImageUrl?.trim() || null;
          initialRecipeImageUrlRef.current = imgUrl;
          recipeImageRemovedRef.current = false;
          recipeImageFileRef.current = null;
          setRecipeImagePreviewUrl(imgUrl);

          setRecipeName(recipe.recipeName || '');
          setAliases((recipe.aliases || []).join(', '));
          setDescription(recipe.description || '');
          setServings(recipe.servings || 1);
          setNutritionServings(recipe.nutritionServings || 1);
          setDietLabels(ensureStringArray(recipe.dietLabels));
          setCuisineTypes(recipe.cuisines || []);
          
          // Fix mealTypes: filter out invalid values like "main-dish" which belong in dishTypes
          const validMealTypes: string[] = MEAL_TYPE_OPTIONS.map(opt => opt.value);
          const receivedMealTypes = recipe.mealTypes || [];
          const filteredMealTypes = receivedMealTypes.filter(mt => validMealTypes.includes(mt));
          
          // Move any dish type values from mealTypes to dishTypes
          const receivedDishTypes = recipe.dishTypes || [];
          const dishTypeValues: string[] = DISH_TYPE_OPTIONS.map(opt => opt.value);
          const misplacedDishTypes = receivedMealTypes.filter(mt => dishTypeValues.includes(mt));
          
          const legacyCookingMethodByDishType: Record<string, string> = {
            grilled: 'grilled',
            baked: 'baked',
            fried: 'fried_deep_fried',
          };
          const normalizedDishTypes = receivedDishTypes.filter((dishType) => !legacyCookingMethodByDishType[dishType]);
          const derivedCookingMethod = recipe.cookingMethod
            || receivedDishTypes.find((dishType) => legacyCookingMethodByDishType[dishType])
            || '';

          // Keep a single active dish type so portion suggestions replace cleanly.
          const combinedDishTypes = [...new Set([...normalizedDishTypes, ...misplacedDishTypes])];
          const selectedDishTypes = combinedDishTypes.length > 0 ? [combinedDishTypes[0]] : [];

          setMealTypes(filteredMealTypes);
          setDishTypes(selectedDishTypes);
          setCookingMethod(derivedCookingMethod ? (legacyCookingMethodByDishType[derivedCookingMethod] ?? derivedCookingMethod) : '');
          setShareWithOthers(recipe.isPublic || false);
          hydrateFromServingSizesRef.current({
            servingSizes: recipe.servingSizes,
            defaultServingId: recipe.defaultServingId,
            defaultServingGrams: recipe.defaultServingGrams,
            dishTypesOverride: selectedDishTypes,
          });
          setPrepTimeMinutes(recipe.prepTimeMinutes || 0);
          setCookTimeMinutes(recipe.cookTimeMinutes || 0);
          setTotalTimeMinutes(recipe.totalTimeMinutes || 0);
          setInstructions(recipe.cookingInstructions.join('\n'));
          setCookingTips(recipe.cookingTips || '');

          
          const mappedIngredients = (recipe.ingredients || []).map(ing => ({
            id: ing.id,
            ingredientId: ing.ingredientId,
            name: ing.name,
            amount: ing.quantity,
            unit: ing.unit,
            amountGrams: ing.amountGrams,
            unitWeightGrams: ing.unitWeightGrams,
            unitWeightMl: ing.unitWeightMl,
            amountMl: ing.amountMl,
            allergens: ing.allergens,
            allergenTagIds: ing.allergenTagIds,
            intoleranceTags: ing.intoleranceTags,
            intoleranceTagIds: ing.intoleranceTagIds,
            yieldFactor: ing.yieldFactor,
            retentionFactor: ing.retentionFactor,
            nutrition: ing.nutrition,
            nutritionData: ing.nutrition ? {
              calories: Number(ing.nutrition.calories) || 0,
              macros: {
                protein: Number(ing.nutrition.protein) || 0,
                carbs: Number(ing.nutrition.carbohydrate ?? ing.nutrition.net_carbs) || 0,
                fat: Number(ing.nutrition.total_fat) || 0,
                fiber: Number(ing.nutrition.dietary_fibre) || 0,
                saturatedFat: Number(ing.nutrition.saturated_fat) || 0,
                alcohol: Number((ing.nutrition as Record<string, unknown>).alcohol ?? (ing.nutrition as Record<string, unknown>).ethyl_alcohol) || 0,
              }
            } : undefined,
            preparationNote: ing.preparationNote,
          }));
          const mappedIngredientDerivedAllergies = deriveAllergensFromIngredients(mappedIngredients);
          const mappedIngredientDerivedAllergySet = new Set(mappedIngredientDerivedAllergies);
          const recipeManualAllergies = normalizeRecipeAllergenList(
            recipe.manualAllergens && recipe.manualAllergens.length > 0
              ? recipe.manualAllergens
              : ensureStringArray(recipe.allergens).filter((value) => {
                  const normalized = normalizeRecipeAllergenList([value])[0] || '';
                  return !mappedIngredientDerivedAllergySet.has(normalized);
                }),
          );
          autoManagedAllergiesRef.current = new Set();
          manualAllergiesRef.current = recipeManualAllergies;
          setAllergies(mergeRecipeAllergens(recipeManualAllergies, mappedIngredientDerivedAllergies));

          const mappedIngredientDerivedIntolerances = deriveIntolerancesFromIngredients(mappedIngredients);
          const mappedIngredientDerivedIntoleranceSet = new Set(mappedIngredientDerivedIntolerances);
          const recipeManualIntolerances = normalizeRecipeIntoleranceList(
            recipe.manualIntoleranceTags && recipe.manualIntoleranceTags.length > 0
              ? recipe.manualIntoleranceTags
              : ensureStringArray(recipe.intoleranceTags).filter((value) => {
                  const normalized = normalizeRecipeIntoleranceList([value])[0] || '';
                  return !mappedIngredientDerivedIntoleranceSet.has(normalized);
                }),
          );
          autoManagedIntolerancesRef.current = new Set();
          manualIntolerancesRef.current = recipeManualIntolerances;
          setIntoleranceTags(mergeRecipeIntolerances(recipeManualIntolerances, mappedIngredientDerivedIntolerances));
          ingredientManagement.setIngredients(mappedIngredients);
        } else {
          setError(response.message || 'Recipe not found');
        }
      } catch (e: any) {
        console.error('Error fetching recipe:', e);
        setError(e.message || 'Failed to load recipe');
        showErrorToast(e, 'Error');
      } finally {
        setLoading(false);
      }
    }

    fetchRecipe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, revokeRecipeImagePreview]);

  const handleUpdateRecipe = async () => {
    if (!recipeName.trim()) {
      showErrorToast(new Error('Please enter a recipe name.'), 'Recipe Name Required');
      return;
    }

    if (ingredientManagement.ingredients.length === 0) {
      showErrorToast(new Error('Please add at least one ingredient.'), 'Ingredients Required');
      return;
    }

    if (mealTypes.length === 0) {
      showErrorToast(new Error('Please select at least one meal type.'), 'Meal Type Required');
      return;
    }

    setIsSaving(true);
    setDuplicateRecipeMessage(null);

    try {
      const servingSizes = buildServingSizes();
      const selectedServingId = typeof portionSizeId === 'string' && portionSizeId.trim().length > 0
        ? portionSizeId.trim().toLowerCase()
        : '';

      if (servingSizes.length === 0) {
        showErrorToast(
          new Error('Add at least one portion size before saving the recipe.'),
          'Portion Sizes Required'
        );
        return;
      }

      if (!selectedServingId) {
        showErrorToast(new Error('Please select a default portion size.'), 'Default Portion Required');
        return;
      }

      const invalidPortions = portionSizes.filter((portion, index) => {
        const servingIdRaw = portion.servingId ?? portion.id ?? portion.name ?? `portion-${index}`;
        const servingId = String(servingIdRaw).trim().toLowerCase();
        const grams = resolvePortionGrams(portion);
        const ml = resolvePortionMl(portion);
        const density = Number(portion.densityGPerMl);
        return servingId.length > 0 && ml != null && (
          !Number.isFinite(grams) || grams <= 0 || !Number.isFinite(density) || density <= 0
        );
      });
      if (invalidPortions.length > 0) {
        showErrorToast(
          new Error('ML values require density and a valid grams value for the same portion size.'),
          'Portion Sizes Invalid'
        );
        return;
      }

      const defaultServing = servingSizes.find((entry) => entry.servingId === selectedServingId);
      if (!defaultServing) {
        showErrorToast(
          new Error('Default portion size must have a valid grams value.'),
          'Default Portion Weight Required'
        );
        return;
      }

      let imageUrlPayload: string | null | undefined;
      if (recipeImageFileRef.current) {
        try {
          imageUrlPayload = await uploadCustomRecipeImageFile(recipeImageFileRef.current, {
            recipeId: id!,
          });
        } catch (e) {
          showErrorToast(e instanceof Error ? e : new Error('Could not upload image'), 'Error');
          setIsSaving(false);
          return;
        }
      } else if (recipeImageRemovedRef.current) {
        imageUrlPayload = null;
      } else if (
        recipeImagePreviewUrl
        && (recipeImagePreviewUrl.startsWith('http')
          || recipeImagePreviewUrl.startsWith('//')
          || recipeImagePreviewUrl.startsWith('s3://'))
      ) {
        imageUrlPayload = recipeImagePreviewUrl;
      }

      const servingsPayload =
        typeof servings === 'number' && Number.isFinite(servings) && servings > 0
          ? servings
          : undefined;

      const recipeData = {
        recipeName,
        aliases: aliases
          .split(',')
          .map((alias) => alias.trim())
          .filter(Boolean),
        description,
        ...(servingsPayload != null ? { servings: servingsPayload } : {}),
        nutritionServings,
        ingredients: ingredientManagement.ingredients.map(ing => ({
          ingredientId: ing.ingredientId,
          name: ing.name,
          quantity: ing.amount,
          unit: ing.unit,
          amountGrams: ing.amountGrams,
          amountMl: ing.amountMl,
          unitWeightGrams: ing.unitWeightGrams,
          unitWeightMl: ing.unitWeightMl,
          yieldFactor: ing.yieldFactor,
          retentionFactor: ing.retentionFactor,
          allergens: ing.allergens,
          allergenTagIds: ing.allergenTagIds,
          intoleranceTags: ing.intoleranceTags,
          intoleranceTagIds: ing.intoleranceTagIds,
          preparationNote: ing.preparationNote,
        })),
        instructions: instructions.split('\n').filter(step => step.trim()),
        cookingTips,
        allergens: allergies,
        manualAllergens: manualAllergiesRef.current,
        dietLabels,
        intoleranceTags,
        manualIntoleranceTags: manualIntolerancesRef.current,
        cuisineTypes,
        mealTypes,
        dishTypes: dishTypes.length > 0 ? dishTypes : ['main-dish'],
        cookingMethod: cookingMethod || undefined,
        prepTimeMinutes: typeof prepTimeMinutes === 'number' ? prepTimeMinutes : 10,
        cookTimeMinutes: typeof cookTimeMinutes === 'number' ? cookTimeMinutes : 20,
        totalTimeMinutes: totalTimeMinutes || 30,
        isPublic: shareWithOthers,
        defaultServingId: selectedServingId || undefined,
        defaultServingGrams: defaultServing.grams,
        ...(servingSizes.length > 0 ? { servingSizes } : {}),
        ...(imageUrlPayload !== undefined ? { imageUrl: imageUrlPayload } : {}),
      };

      const response = await updateCustomRecipe(id!, recipeData);

      if (response.success) {
        navigate(`/recipes/${id}`);
      } else {
        throw new Error(response.message || 'Failed to update recipe');
      }
    } catch (error) {
      console.error('Error updating recipe:', error);
      const conflictMessage = getPublicRecipeNameConflictMessage(error);
      if (conflictMessage) {
        setDuplicateRecipeMessage(conflictMessage);
        return;
      }
      showErrorToast(error, 'Error');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading recipe...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/recipes')}>Back to Recipes</Button>
        </div>
      </div>
    );
  }

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '22px',
          bgcolor: '#FBFBFB',
        }}
      >
        <div className="min-h-full bg-[#FBFBFB]">
          <div className="h-full w-full px-8 py-8">
            <div
              className="w-full min-w-0"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <div className="mb-6 flex w-full min-w-0 items-center gap-2 rounded-lg border border-[#0000001A] px-3 py-2 text-[13px] text-slate-400">
                <Link to="/recipes" className="shrink-0 hover:text-slate-600">
                  Recipes
                </Link>
                <span className="shrink-0">/</span>
                <span className="truncate font-medium text-slate-800">Edit Recipe</span>
              </div>
              <h1
                className="text-[32px] font-medium capitalize leading-tight text-slate-900"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                Edit Recipe
              </h1>
              <Accordion
                type="multiple"
                defaultValue={['basic', 'portions', 'categories', 'ingredients', 'instructions']}
                className="mt-6 flex flex-col gap-[35px] overflow-visible"
              >
                <AccordionItem
                  value="basic"
                  className="overflow-visible rounded-xl border border-[#E5E7EB] bg-white shadow-sm"
                >
                  <AccordionTrigger
                    className="px-5 py-4 text-[24px] font-medium text-slate-900 hover:no-underline sm:px-6"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                  >
                    Recipe Information
                  </AccordionTrigger>
                  <AccordionContent className="overflow-visible px-5 pb-10 pt-2 sm:px-6">
                    <RecipeInformationForm
                      recipeName={recipeName}
                      aliases={aliases}
                      description={description}
                      servings={servings}
                      dishTypes={dishTypes}
                      mealTypes={mealTypes}
                      cookingMethod={cookingMethod}
                      prepTimeMinutes={prepTimeMinutes}
                      cookTimeMinutes={cookTimeMinutes}
                      totalTimeMinutes={totalTimeMinutes}
                      shareWithOthers={shareWithOthers}
                      recipeImagePreviewUrl={recipeImagePreviewUrl}
                      nutritionServings={nutritionServings}
                      onNutritionServingsChange={setNutritionServings}
                      variant="edit"
                      onRecipeNameChange={setRecipeName}
                      onAliasesChange={setAliases}
                      onDescriptionChange={setDescription}
                      onServingsChange={setServings}
                      onDishTypesChange={setDishTypes}
                      onMealTypesChange={setMealTypes}
                      onCookingMethodChange={setCookingMethod}
                      onPrepTimeChange={(value) => {
                        setPrepTimeMinutes(value);
                        const cookTime = typeof cookTimeMinutes === 'number' ? cookTimeMinutes : 0;
                        setTotalTimeMinutes((typeof value === 'number' ? value : 0) + cookTime);
                      }}
                      onCookTimeChange={(value) => {
                        setCookTimeMinutes(value);
                        const prepTime = typeof prepTimeMinutes === 'number' ? prepTimeMinutes : 0;
                        setTotalTimeMinutes(prepTime + (typeof value === 'number' ? value : 0));
                      }}
                      onShareWithOthersChange={setShareWithOthers}
                      onRecipeImageFileSelect={handleRecipeImageFileSelect}
                    />
                  </AccordionContent>
                </AccordionItem>

          {/* Portions Section */}
          <AccordionItem
            value="portions"
            className="relative z-50 overflow-visible rounded-[20px] border border-[#F5F5F5] bg-white shadow-sm"
          >
            <AccordionTrigger
              className="px-5 py-4 text-left text-[24px] font-medium leading-[1.334] text-[rgba(0,0,0,0.87)] hover:no-underline sm:px-5"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Portion Sizes
            </AccordionTrigger>
            <AccordionContent className="overflow-visible px-5 pb-[26px] pt-5 sm:px-5">
              <RecipePortionSizesSection
                portionSizeId={portionSizeId}
                portionSizes={portionSizes}
                defaultServingGrams={defaultServingGrams}
                portionDensityGPerMl={portionDensityGPerMl}
                customPortionName={customPortionName}
                customPortionGrams={customPortionGrams}
                customPortionMl={customPortionMl}
                onPortionSizeChange={setPortionSizeId}
                onDefaultServingGramsChange={setDefaultServingGrams}
                onPortionDensityChange={setPortionDensityGPerMl}
                onPortionWeightChange={setPortionWeight}
                onPortionMlChange={setPortionVolume}
                onPortionQuantityChange={portionConfig.setPortionQuantity}
                onCustomPortionNameChange={setCustomPortionName}
                onCustomPortionGramsChange={setCustomPortionGrams}
                onCustomPortionMlChange={setCustomPortionMl}
                onAddCustomPortion={addCustomPortion}
                onRemovePortion={removePortion}
                getPortionOptionValue={getPortionOptionValue}
              />
            </AccordionContent>
          </AccordionItem>

          {/* Categories Section */}
          <AccordionItem
            value="categories"
            className="relative z-40 overflow-visible rounded-[20px] border border-[#F5F5F5] bg-white shadow-sm"
          >
            <AccordionTrigger
              className="px-5 py-4 text-left text-[24px] font-medium leading-[1.334] text-[rgba(0,0,0,0.87)] hover:no-underline sm:px-5"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Tags
            </AccordionTrigger>
            <AccordionContent className="overflow-visible px-5 pb-[26px] pt-5 sm:px-5">
              <RecipeTagsSection
                allergies={allergies}
                intoleranceTags={intoleranceTags}
                cuisineTypes={cuisineTypes}
                dietLabels={dietLabels}
                lockedAllergies={ingredientDerivedAllergies}
                lockedIntoleranceTags={ingredientDerivedIntolerances}
                onAllergiesChange={handleAllergiesChange}
                onIntoleranceTagsChange={handleIntoleranceTagsChange}
                onCuisineTypesChange={setCuisineTypes}
                onDietLabelsChange={setDietLabels}
              />
            </AccordionContent>
          </AccordionItem>

          {/* Ingredients Section */}
          <AccordionItem
            value="ingredients"
            className="relative z-30 overflow-visible rounded-[20px] border border-[#F5F5F5] bg-white shadow-sm"
          >
            <AccordionTrigger
              className="items-start px-5 py-4 hover:no-underline sm:px-5"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <div className="flex flex-col gap-2 text-left">
                <span className="text-[24px] font-medium leading-[1.334] text-[rgba(0,0,0,0.87)]">
                  Ingredients
                </span>
                <span className="text-sm font-normal leading-[1.334] text-[rgba(0,0,0,0.8)]">
                  Physical information needed for accurate nutritional calculations
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="!overflow-visible px-5 pb-[26px] pt-5 sm:px-5">
              <IngredientsSection
                ingredients={ingredientManagement.ingredients}
                isAddingIngredient={ingredientManagement.isAddingIngredient}
                ingredientSearchQuery={ingredientManagement.ingredientSearchQuery}
                selectedIngredientName={ingredientManagement.selectedIngredientName}
                selectedIngredientPreviewUrl={ingredientManagement.selectedIngredientPreviewUrl}
                selectedYieldFactor={ingredientManagement.selectedYieldFactor}
                selectedRetentionFactor={ingredientManagement.selectedRetentionFactor}
                ingredientAmount={ingredientManagement.ingredientAmount}
                ingredientUnit={ingredientManagement.ingredientUnit}
                unitWeightGrams={ingredientManagement.unitWeightGrams}
                unitWeightMl={ingredientManagement.unitWeightMl}
                effectiveAmountGrams={ingredientManagement.effectiveAmountGrams}
                effectiveAmountMl={ingredientManagement.effectiveAmountMl}
                gramsPerMl={ingredientManagement.gramsPerMl}
                availableUnits={ingredientManagement.availableUnits}
                unitLabelsByKey={ingredientManagement.unitLabelsByKey}
                unitWeightsByKey={ingredientManagement.unitWeightsByKey}
                nutritionData={ingredientManagement.nutritionData}
                isLoadingNutrition={ingredientManagement.isLoadingNutrition}
                isLoadingUnits={ingredientManagement.isLoadingUnits}
                editingIngredientId={ingredientManagement.editingIngredientId}
                showIngredientResults={ingredientManagement.showIngredientResults}
                ingredientSearchResults={ingredientManagement.ingredientSearchResults}
                isSearchingIngredients={ingredientManagement.isSearchingIngredients}
                bulkIngredientInput={ingredientManagement.bulkIngredientInput}
                isBulkAddingIngredients={ingredientManagement.isBulkAddingIngredients}
                totalMacros={ingredientManagement.totalMacros}
                servings={typeof servings === 'number' ? servings : 1}
                onStartAddIngredient={ingredientManagement.startAddIngredient}
                onSearchQueryChange={ingredientManagement.setIngredientSearchQuery}
                onIngredientSelect={ingredientManagement.handleSelectIngredient}
                onAmountChange={ingredientManagement.handleQuantityChange}
                onUnitChange={ingredientManagement.handleUnitChange}
                onUnitWeightChange={ingredientManagement.handleUnitWeightChange}
                onUnitWeightMlChange={ingredientManagement.handleUnitWeightMlChange}
                onCloseDropdown={ingredientManagement.handleCloseIngredientDropdown}
                onSubmitIngredient={ingredientManagement.handleSubmitIngredient}
                onCancelIngredient={ingredientManagement.closeIngredientForm}
                onEditIngredient={ingredientManagement.handleEditIngredient}
                onRemoveIngredient={ingredientManagement.handleRemoveIngredient}
                onBulkIngredientInputChange={ingredientManagement.setBulkIngredientInput}
                onBulkAddIngredients={ingredientManagement.handleBulkAddIngredients}
              />
            </AccordionContent>
          </AccordionItem>

          {/* Instructions Section */}
          <AccordionItem
            value="instructions"
            className="relative z-20 overflow-visible rounded-[20px] border border-[#F5F5F5] bg-white shadow-sm"
          >
            <AccordionTrigger
              className="items-start px-5 py-4 hover:no-underline sm:px-5"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <div className="flex flex-col gap-2 text-left">
                <span className="text-[24px] font-medium leading-[1.334] text-[rgba(0,0,0,0.87)]">
                  Instructions
                </span>
                <span className="text-sm font-normal leading-[1.334] text-[rgba(0,0,0,0.8)]">
                  Describe how to prepare the recipe and any optional tips.
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="overflow-visible px-5 pb-[26px] pt-5 sm:px-5">
              <InstructionsSection
                instructions={instructions}
                cookingTips={cookingTips}
                onInstructionsChange={setInstructions}
                onCookingTipsChange={setCookingTips}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

              <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-3">
                <Button
                  variant="ghost"
                  className="h-8 px-3 text-xs text-slate-500 hover:bg-slate-100"
                  type="button"
                  onClick={() => navigate('/recipes')}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleUpdateRecipe}
                  disabled={isSaving || !recipeName.trim() || ingredientManagement.ingredients.length === 0}
                  className="h-8 bg-[#2EB4E4] px-3 text-xs font-semibold text-white hover:bg-[#22a8d8]"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <ChefHat className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Paper>
      <AlertDialog
        open={Boolean(duplicateRecipeMessage)}
        onOpenChange={(open) => {
          if (!open) setDuplicateRecipeMessage(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recipe name already exists</AlertDialogTitle>
            <AlertDialogDescription>
              {duplicateRecipeMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setDuplicateRecipeMessage(null)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};

export default EditCustomRecipePage;
